/**
 * ScheduleStatusBannerPure
 *
 * Pure presentational component for schedule status messages.
 * No server dependencies - all data passed via props.
 */
import { ButtonLink } from '../Input2';
import { Alerts, Time } from '../StrokeIcons';

// ============================================================================
// Types
// ============================================================================

export type ScheduleStatus = {
  isConfigured: boolean;
  isWithinSchedule: boolean;
  nextWindowStartMs: number | null;
  nextWindowStartFormatted: string | null;
  settingsUrl: string;
};

export type ScheduleStatusBannerPureProps = {
  scheduleStatus: ScheduleStatus | undefined | null;
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleStatusBannerPure({ scheduleStatus }: ScheduleStatusBannerPureProps) {
  if (!scheduleStatus) return null;

  if (!scheduleStatus.isConfigured) {
    return (
      <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-6 py-3 dark:border-amber-800 dark:bg-amber-900/20">
        <Alerts.AlertTriangle className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 text-sm text-amber-800 dark:text-amber-200">
          LinkedIn schedule not configured. Messages will not be sent until you set up your schedule.
        </div>
        <ButtonLink
          href={scheduleStatus.settingsUrl}
          text
          className="whitespace-nowrap text-sm font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100">
          Configure schedule
        </ButtonLink>
      </div>
    );
  }

  if (!scheduleStatus.isWithinSchedule) {
    return (
      <div className="flex items-center gap-3 border-b border-blue-200 bg-blue-50 px-6 py-3 dark:border-blue-800 dark:bg-blue-900/20">
        <Time.Clock className="size-5 shrink-0 text-blue-600 dark:text-blue-400" />
        <div className="flex-1 text-sm text-blue-800 dark:text-blue-200">
          Outside scheduled hours. Messages will start sending{' '}
          <span className="font-medium">
            {scheduleStatus.nextWindowStartFormatted ?? 'at your next scheduled time'}
          </span>
          .
        </div>
        <ButtonLink
          href={scheduleStatus.settingsUrl}
          text
          className="whitespace-nowrap text-sm font-medium text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100">
          View schedule
        </ButtonLink>
      </div>
    );
  }

  return null;
}
